import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import "../styles/MemberDetails.css";
import { FaRegUserCircle } from "react-icons/fa";
import {
  MdOutlineLocationOn,
  MdUpdate,
  MdStore,
  MdOutlineAssignmentInd,
} from "react-icons/md";

function RetailShopDetails() {
  const location = useLocation();
  const retail = location.state?.retail;
  const navigate = useNavigate();

  if (!retail) {
    return (
      <div className="member-details-layout">
        <SideBar />
        <div className="member-details-main">
          <div style={{ padding: "30px", textAlign: "center" }}>
            <h3>No Retailer Data Available</h3>
            <p>Please go back and select a retailer to view details.</p>
            <Link to="/manage-retailer" className="back-link">
              ← Back to Manage Retailer
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const goToWallet = () => {
    navigate("/retail-wallet-details", { state: { retail } });
  };

  const goToRequestHistory = () => {
    navigate("/retail-cash-request-history", { state: { retail } });
  };

  const goToEdit = () => {
    navigate("/edit-retail-profile", { state: { retail } });
  };

  // Helper function to handle missing data
  const getDisplayValue = (value, fallback = "Not specified") => {
    return value && value !== "" ? value : fallback;
  };

  return (
    <div className="member-details-layout">
      <SideBar />

      <div className="member-details-main">
        {/* Header */}
        <div className="details-header">
          <h2 style={{ width: "300px" }}>Retail Business Details</h2>
          <p>You can view and manage retail business details</p>
        </div>

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/dashboard" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <Link to="/manage-retailer" className="breadcrumb-link">
            Manage Retail Business
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-current">Retailer Shop Details</span>
        </div>

        {/* Tab buttons */}
        <div className="top-bar">
          <div className="top-buttons">
            <button className="tab-btn active">Personal Details</button>
            <button className="tab-btn" onClick={goToWallet}>
              Wallet Details
            </button>
            <button className="tab-btn" onClick={goToRequestHistory}>
              Cash Request History
            </button>
          </div>
        </div>

        {/* Owner Details */}
        <div className="section">
          <div className="edit-btn-wrap">
            <button className="edit-btn" onClick={goToEdit}>
              Edit
            </button>
          </div>
          <h3>
            <FaRegUserCircle /> Owner Details
          </h3>
          <div className="section-content">
            <p>
              <strong>Owner Name:</strong> {getDisplayValue(retail.fullName)}
            </p>
            <p>
              <strong>First Name:</strong>{" "}
              {getDisplayValue(retail.first_name || retail.firstName)}
            </p>
            <p>
              <strong>Last Name:</strong>{" "}
              {getDisplayValue(retail.last_name || retail.surname)}
            </p>
          </div>
        </div>

        {/* Business Details */}
        <div className="section">
          <h3>
            <MdStore /> Business Details
          </h3>
          <div className="section-content">
            {retail.photo_url ? (
              <div style={{ marginBottom: "15px" }}>
                <img
                  src={retail.photo_url}
                  alt="Owner"
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #ddd",
                  }}
                />
              </div>
            ) : (
              <div style={{ marginBottom: "15px", color: "#666" }}>
                <FaRegUserCircle size={50} />
                <p>No photo available</p>
              </div>
            )}
            <p>
              <strong>Business Name:</strong>{" "}
              {getDisplayValue(
                retail.shop_details?.retail_name || retail.retailName
              )}
            </p>
            <p>
              <strong>Email:</strong> {getDisplayValue(retail.email)}
            </p>
            <p>
              <strong>Mobile:</strong>{" "}
              {getDisplayValue(retail.phone_number || retail.mobile)}
            </p>
            <p>
              <strong>Type of Industry:</strong>{" "}
              {getDisplayValue(
                retail.shop_details?.retail_type || retail.industry
              )}
            </p>
            <p>
              <strong>Retail Sub Type:</strong>{" "}
              {getDisplayValue(
                retail.shop_details?.retail_sub_type || retail.retailSubType
              )}
            </p>
            <p>
              <strong>Website:</strong>{" "}
              {getDisplayValue(retail.shop_details?.website)}
            </p>
            <p>
              <strong>Contract Signed:</strong>{" "}
              {retail.shop_details?.signed_contract ? "Yes" : "No"}
            </p>
            <p>
              <strong>Business Status:</strong>{" "}
              {getDisplayValue(retail.shop_details?.status)}
            </p>
          </div>
        </div>

        {/* Business Address */}
        <div className="section">
          <h3>
            <MdOutlineLocationOn /> Business Address
          </h3>
          <div className="section-content">
            <p>
              <strong>Address line 1:</strong>{" "}
              {getDisplayValue(retail.address_line1 || retail.address?.line1)}
            </p>
            <p>
              <strong>Address line 2:</strong>{" "}
              {getDisplayValue(retail.address_line2 || retail.address?.line2)}
            </p>
            <p>
              <strong>Address line 3:</strong>{" "}
              {getDisplayValue(retail.address_line3 || retail.address?.line3)}
            </p>
            <p>
              <strong>City:</strong>{" "}
              {getDisplayValue(retail.city || retail.address?.city)}
            </p>
            <p>
              <strong>Region:</strong>{" "}
              {getDisplayValue(retail.region || retail.address?.county)}
            </p>
            <p>
              <strong>Postcode:</strong>{" "}
              {getDisplayValue(retail.postcode || retail.address?.postcode)}
            </p>
            <p>
              <strong>Country:</strong>{" "}
              {getDisplayValue(retail.country || retail.address?.country)}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="section">
          <h3>
            <MdUpdate /> Status
          </h3>
          <div className="section-content">
            <p>
              <strong>Email Status:</strong>
              <span
                className={`status ${(
                  retail.current_status || retail.status
                )?.toLowerCase()}`}
              >
                {getDisplayValue(retail.current_status || retail.status)}
              </span>
            </p>
            <p>
              <strong>Notes:</strong>{" "}
              {getDisplayValue(
                retail.status_note || retail.notes,
                "No notes available"
              )}
            </p>
          </div>
        </div>

        {/* Referred By */}
        <div className="section">
          <h3>
            <MdOutlineAssignmentInd /> Referred By
          </h3>
          <div className="section-content">
            <p>
              <strong>Referrer ID:</strong> {retail.referrer_id || "N/A"}
            </p>
            <p>
              <strong>Referrer:</strong> {retail.referrer_name || "N/A"}
            </p>

            <p>
              <strong>Referrer Agent Type:</strong>{" "}
              {retail.referrer_agent_type}
            </p>
            <p>
              <strong>Referrer Country:</strong>{" "}
              {retail.referrer_country || "N/A"}
            </p>
            <p>
              <strong>Referrer Region:</strong>{" "}
              {retail.referrer_region || "N/A"}
            </p>
            <p>
              <strong>Referrer Email:</strong> {retail.referrer_email || "N/A"}
            </p>
            <p>
              <strong>Referrer Phone:</strong> {retail.referrer_phone || "N/A"}
            </p>
          </div>
        </div>

        {/* Additional Information */}
        <div className="section">
          <h3>Additional Information</h3>
          <div className="section-content">
            <p>
              <strong>Created Date:</strong>{" "}
              {retail.created_at
                ? new Date(retail.created_at).toLocaleDateString()
                : "Not available"}
            </p>
            <p>
              <strong>Last Updated:</strong>{" "}
              {retail.updated_at
                ? new Date(retail.updated_at).toLocaleDateString()
                : "Not available"}
            </p>
            {retail.has_keys_generated && (
              <p>
                <strong>Key Status:</strong> Keys Generated ✓
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RetailShopDetails;
